import { useEffect, useMemo, useState } from 'react';
import {
  createJobRole as createJobRoleApiRequest,
  deleteJobRole as deleteJobRoleApiRequest,
  editJobRole as editJobRoleApiRequest,
  getJobRoles as getJobRolesApiRequest,
} from 'src/api/job-role';
import { useAuthContext } from 'src/auth/hooks';
import { useOrganizationContext } from 'src/hooks/use-organization-context';
import { JobRole } from 'src/models/job-role';
import { JobRoleContext } from './job-role-context-types';

interface JobRoleProviderProps {
  children: React.ReactNode;
}

const JobRoleProvider: React.FC<JobRoleProviderProps> = ({ children }) => {
  const { currentOrg } = useOrganizationContext();
  const { user } = useAuthContext();

  const [jobRoles, setJobRoles] = useState<JobRole[]>([]);

  useEffect(() => {
    if (!currentOrg) return;
    getJobRolesApiRequest(currentOrg).then((res) => {
      if (res.success) {
        setJobRoles(res.data);
      }
    });
  }, [currentOrg]);

  const myJobRoles = useMemo(() => {
    if (user?._id && jobRoles?.length) {
      return jobRoles.filter((d) => user.jobRoles.includes(d._id));
    }
    return [];
  }, [user?._id, jobRoles, user?.jobRoles]);

  const providerValue = useMemo(() => {
    const getJobRoles = (): JobRole[] => {
      if (jobRoles) {
        return jobRoles;
      }
      if (currentOrg && !jobRoles) {
        getJobRolesApiRequest(currentOrg).then((res) => {
          if (res.success) {
            setJobRoles(res.data);
          }
        });
      }
      return [];
    };

    const getMyJobRoles = (): JobRole[] => myJobRoles;

    const createJobRole = async (
      jobRole: Omit<JobRole, '_id' | 'organization'>
    ): Promise<string | null> =>
      createJobRoleApiRequest(jobRole).then((res) => {
        if (res.success) {
          setJobRoles([...jobRoles, res.data!]);
          return null;
        }
        return res.error ?? 'Error creating job role';
      });

    const editJobRole = (jobRole: Partial<JobRole> & { _id: string }): void => {
      if (jobRole.skills !== undefined) {
        setJobRoles((prev) => prev.map((d) => (d._id === jobRole._id ? { ...d, ...jobRole } : d)));
        return;
      }

      editJobRoleApiRequest(jobRole).then((res) => {
        if (res.success && res.data) {
          setJobRoles((prev) => prev.map((d) => (d._id === res.data!._id ? res.data! : d)));
        }
      });
    };

    const deleteJobRole = (jobRoleId: string): void => {
      deleteJobRoleApiRequest(jobRoleId).then((res) => {
        if (res.success) {
          const filtered = jobRoles.filter((d) => d._id !== jobRoleId);
          setJobRoles(filtered);
        }
      });
    };

    return { getMyJobRoles, createJobRole, editJobRole, deleteJobRole, getJobRoles };
  }, [currentOrg, jobRoles, myJobRoles]);

  return <JobRoleContext.Provider value={providerValue}>{children}</JobRoleContext.Provider>;
};

export default JobRoleProvider;
